import React, { useEffect, useRef, useState } from "react";
import * as Chart from "chart.js";

const CHART_HEIGHT = 185;

const AreaChart = ({
  data = [],
  metrics = [],
  headerBadge = "",
  mainTitle = "",
  yAxisLabel = "MONTHLY DAILY VOLUMES",
  firstColumnLabel = "",
  tableRowLabel = "",
  areaStartColor = "var(--green-medium)",
  areaEndColor = "var(--green-medium)",
  headerBgColor = "var(--green-medium)",
  sidebarBgColor = "var(--!border-blue)",
  sidebarBorderColor = "var(--!border-blue)",
  title,
  subTitle,
  tableHeaderFontSize = "6px",   // time labels (00, 01, 02...)
tableValueFontSize = "6px",    // values (1,234)
tableLabelFontSize = "6px",    // "Collection" label
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const containerRef = useRef(null);
  // % of chart width taken up by the y-axis gutter — table's first column
  // is sized to match this so its columns line up with the chart's plot area
  const [leftColPct, setLeftColPct] = useState(8);

  const buildChart = (width, height) => {
    if (!chartRef.current || data.length === 0) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    // ✅ Manually set canvas pixel dimensions — bypass transform confusion.
    // Chart.js applies the actual devicePixelRatio scaling itself (via the
    // `devicePixelRatio` option below) — with responsive:false it otherwise
    // defaults to 1x regardless of the screen, which is what caused the blur.
    // Force at least 2x so text/labels stay crisp even on standard (non-retina) screens.
    const dpr = Math.max(window.devicePixelRatio || 1, 2);
    chartRef.current.width = width;
    chartRef.current.height = height;
    // Chart.js only auto-sets canvas.style width/height on the very first
    // build (retinaScale skips it once a style is already present) — since
    // we destroy + rebuild the whole chart on every resize, a stale style
    // from an earlier build would stay while the pixel buffer moves on,
    // stretching/misplacing the rendered content. Keep it in sync ourselves.
    chartRef.current.style.width = `${width}px`;
    chartRef.current.style.height = `${height}px`;

    const ctx = chartRef.current.getContext("2d");

    const startColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue(areaStartColor.replace("var(", "").replace(")", ""))
        .trim() || areaStartColor;

    const endColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue(areaEndColor.replace("var(", "").replace(")", ""))
        .trim() || areaEndColor;

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, startColor + "F2");
    gradient.addColorStop(1, endColor + "66");

    Chart.Chart.register(
      Chart.LineController,
      Chart.LineElement,
      Chart.PointElement,
      Chart.LinearScale,
      Chart.CategoryScale,
      Chart.Filler,
      Chart.Tooltip,
      Chart.Title
    );

    const textColor = "#e5e7eb";

    const dataLabelsPlugin = {
  id: "dataLabels",
  afterDatasetsDraw: (chart) => {
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      const points = meta.data
        .map((point, index) => ({
          x: Math.min(
            Math.max(point.x, chartArea.left + 20),
            chartArea.right - 20
          ),
          y: point.y,
          value: dataset.data[index],
          index,
        }))
        .filter((p) => p.value != null && p.value > 0);

      if (!points.length) return;

      // Pehle font set karo taaki measureText accurate ho
      ctx.font = "bold 11px sans-serif";

      // Har point ka actual text width measure karo
      const pointsWithWidth = points.map((p) => ({
        ...p,
        label: p.value.toLocaleString(),
        halfW: ctx.measureText(p.value.toLocaleString()).width / 2 + 3,
      }));

      const DY_OFFSETS = [-40, -55, -25, -70, -85, -100, -15, -115, -130, 18, 30];
      const MIN_DY  = 15;
      const placed  = []; // { x, labelY, halfW }

      // Do rectangles overlap karte hain?
      const overlaps = (ax, ay, aHalfW, bx, by, bHalfW) =>
        Math.abs(ax - bx) < (aHalfW + bHalfW) &&
        Math.abs(ay - by) < MIN_DY;

      pointsWithWidth.forEach((p) => {
        let chosen = null;

        // Edge points get x-clamped to nearly the same X (see clamp above), so
        // a dy-only offset can still stack labels directly on top of one
        // another. dx is sized off this label's own half-width (+ a margin)
        // so the horizontal nudge is always wide enough to actually clear —
        // a fixed small dx wasn't enough for wider (5-digit) labels.
        const shiftX = p.halfW + 8;
        const CANDIDATES = [
          ...DY_OFFSETS.map((dy) => ({ dx: 0, dy })),
          ...DY_OFFSETS.map((dy) => ({ dx: -shiftX, dy })),
          ...DY_OFFSETS.map((dy) => ({ dx: shiftX, dy })),
        ];

        for (const { dx, dy } of CANDIDATES) {
          const candidateX = p.x + dx;
          const candidateY = p.y + dy;
          if (candidateY < chartArea.top + 5) continue;

          const clash = placed.some((pl) =>
            overlaps(candidateX, candidateY, p.halfW, pl.x, pl.labelY, pl.halfW)
          );

          if (!clash) {
            chosen = { x: candidateX, y: candidateY };
            break;
          }
        }

        // Sab candidates clash kar rahe hon to jo sabse kam collide kare wahi use karo
        // — pehle hamesha ek fixed spot par stack ho jate the, ab bhi separate rahenge
        if (chosen === null) {
          let best = { x: p.x, y: chartArea.top + 10 };
          let fewestClashes = Infinity;

          for (const { dx, dy } of CANDIDATES) {
            const candidateX = p.x + dx;
            const candidateY = p.y + dy;
            if (candidateY < chartArea.top + 5) continue;

            const clashCount = placed.filter((pl) =>
              overlaps(candidateX, candidateY, p.halfW, pl.x, pl.labelY, pl.halfW)
            ).length;

            if (clashCount < fewestClashes) {
              fewestClashes = clashCount;
              best = { x: candidateX, y: candidateY };
            }
          }

          chosen = best;
        }

        placed.push({ x: chosen.x, labelY: chosen.y, halfW: p.halfW });

        ctx.save();
        ctx.fillStyle    = "#ffffff";
        ctx.font         = "bold 11px sans-serif";
        ctx.textAlign    = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(p.label, chosen.x, chosen.y);
        ctx.restore();
      });
    });
  },
};

    // Keeps the time-table's first column in sync with the chart's actual
    // y-axis gutter width, so table columns line up under the plot area.
    const gutterSyncPlugin = {
      id: "gutterSync",
      afterLayout: (chart) => {
        const pct = (chart.chartArea.left / chart.width) * 100;
        if (Number.isFinite(pct) && pct > 0) {
          setLeftColPct((prev) => (Math.abs(prev - pct) > 0.2 ? pct : prev));
        }
      },
    };

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "line",
      data: {
        labels: data.map((d) => d.time),
        datasets: [
          {
            data: data.map((d) => d.value),
            borderColor: "rgba(0,0,0,0)",
            backgroundColor: gradient,
            borderWidth: 1.5,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointBackgroundColor: startColor,
            pointBorderColor: "#ffffff",
            pointBorderWidth: 1,
            categoryPercentage: 1.0,
            barPercentage: 1.0,
            clip: false,
          },
        ],
      },
      plugins: [dataLabelsPlugin, gutterSyncPlugin],
      options: {
        // ✅ responsive: false — hum khud size control kar rahe hain
        responsive: false,
        maintainAspectRatio: false,
        animation: false,
        devicePixelRatio: Math.max((window.devicePixelRatio || 1) * 2, 3),

        // With responsive:false, Chart.js's own retinaScale() defaults to a
        // 1x ratio unless told otherwise — pass the real screen ratio so the
        // canvas buffer is sharp instead of blurry on scaled/high-DPI displays.
        devicePixelRatio: dpr,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            borderColor: "#475569",
            borderWidth: 1,
            padding: 12,
            displayColors: false,
            callbacks: {
              title: (context) => context[0].label,
              label: (context) =>
                `Value: ${context.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: { display: false, offset: false },
          y: {
            ticks: {
              color: textColor,
              font: { size: 9, weight: "600" },
              callback: (value) => value.toLocaleString(),
            },
            grid: { display: false },
            afterDataLimits: (scale) => {
              scale.max = scale.max * 1.15;
            },
            title: {
              display: true,
              text: yAxisLabel,
              color: textColor,
              font: { size: 9, weight: "600" },
            },
          },
        },
        layout: {
          padding: { left: 0, right: 0, top: 35, bottom: 0 },
        },
      },
    });
  };

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;

    // ✅ offsetWidth/offsetHeight: transform-independent actual DOM size
    // getBoundingClientRect() scale factor include karta hai — isliye use nahi karna
    const w = containerRef.current.offsetWidth;
    const h = CHART_HEIGHT;

    buildChart(w, h);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // offsetWidth use karo — scale transform ko ignore karta hai
        const newW = entry.target.offsetWidth;
        const newH = CHART_HEIGHT;
        if (!newW) continue;

        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
        buildChart(newW, newH);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, areaStartColor, areaEndColor, yAxisLabel]);

  return (
    <div
      className="text-foreground transition-colors w-full"
      style={{ backgroundColor: "#464647" }}
    >
      {title || subTitle ? (
        <div
          className="text-center"
          style={{ backgroundColor: "#ffffff", padding: "1rem" }}
        >
          <h1 className="text-2xl font-bold" style={{ color: "#000000" }}>
            {title}
          </h1>
          {subTitle && (
            <h2 className="text-xl font-bold" style={{ color: "#000000" }}>
              {subTitle}
            </h2>
          )}
        </div>
      ) : null}

      <div className="w-full flex flex-col px-2 pt-2" style={{ minHeight: 0 }}>
        <div
          className="relative flex items-start justify-between mb-0"
          style={{ minHeight: "0.75rem" }}
        >
          {headerBadge && (
            <div
              className="px-2 py-1 rounded text-xs font-semibold text-white flex-shrink-0"
              style={{ backgroundColor: headerBgColor }}
            >
              {headerBadge}
            </div>
          )}

          <div
            className="absolute left-0 right-0 text-center text-sm font-bold pointer-events-none"
            style={{ color: "#ffffff", top: 0 }}
          >
            {mainTitle}
          </div>

          {metrics.length > 0 && (
            <div
              className="ml-auto text-[10px] rounded shadow-sm flex-shrink-0"
              style={{ borderColor: sidebarBorderColor }}
            >
              <table className="!border-collapse">
                <tbody>
                  {metrics.map((m, i) => (
                    <tr key={i}>
                      <td
                        className="px-1 py-0.5 !border font-medium"
                        style={{
                          borderColor: sidebarBorderColor,
                          backgroundColor: "#464647",
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.label}
                      </td>
                      <td
                        className="px-1 py-0.5 !border font-bold"
                        style={{
                          borderColor: sidebarBorderColor,
                          backgroundColor: "#464647",
                          color: "#ffffff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/*
          ✅ containerRef yahan — offsetWidth se actual unscaled width milti hai
          canvas ko CSS width/height mat do — Chart.js canvas.width/height se control karta hai
        */}
        <div
          ref={containerRef}
          style={{
            height: `${CHART_HEIGHT}px`,
            width: "100%",
            position: "relative",
          }}
        >
          <canvas ref={chartRef} />
        </div>

        <div className="w-full" style={{ marginTop: "2px" }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: `${leftColPct}%` }} />
              {data.map((_, i) => (
                <col key={i} style={{ width: `${(100 - leftColPct) / data.length}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
  style={{
    color: "#ffffff",
    border: "1px solid #ffffff",
    padding: "2px 2px",
    textAlign: "left",
    fontSize: tableLabelFontSize,
    fontWeight: 600,
  }}
>
  {firstColumnLabel}
</th>
                {data.map((d, i) => (
                  <th
                    key={i}
                    style={{
                      color: "#ffffff",
                      border: "1px solid #ffffff",
                      padding: "1px 0px",
                      textAlign: "center",
                      fontSize: tableHeaderFontSize,

                      fontWeight: 500,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.time}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid #ffffff",
                    padding: "1px 2px",
                    color: "#ffffff",
                    fontSize: tableLabelFontSize,
                    fontWeight: 500,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                  title={tableRowLabel}
                >
                  {tableRowLabel}
                </td>
                {data.map((d, i) => (
                  <td
                    key={i}
                    style={{
                      backgroundColor:
                        i % 2 === 0 ? "transparent" : "#5a5a5b",
                      color: "#ffffff",
                      border: "1px solid #ffffff",
                      padding: "1px 0px",
                      textAlign: "center",
                      fontSize: tableHeaderFontSize,

                      fontWeight: 500,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(d.value ?? 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div
            className="text-center font-semibold py-1"
            style={{ color: "#ffffff", fontSize: "6px" }}
          >
            TIME
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaChart;